// src/components/sidebar/AppSidebar.tsx

import { Link } from '@tanstack/react-router';
import { useMyPermissions } from "@shared/hooks/data/usePermissionMatrix";
import BrandLogo from '@/components/common/BrandLogo';
import { useLocation } from '@tanstack/react-router';
import Image from 'next/image';
import { useSidebar } from "@/context/SidebarContext";
import { getRoutesGroupedByCategory, featureMatches } from "@/config/routes";
import { ChevronDownIcon, HorizontaLDots } from "../icons";
import * as LucideIcons from "lucide-react";
import React, { useCallback, useRef, useState, useEffect } from "react";
import { useAuth } from "@/hooks/auth/useAuth";
import { useBilling } from "@shared/subscription";
import { useTenantSubscription } from "@/hooks/data/useSubscriptions";

// RouteConfig.icon is a Lucide icon *name* (shared-core/src/config/routes.tsx), not a
// component — was previously rendered as `{route.icon}` directly, which just prints the
// string ("LayoutGrid", "MapPin", ...) next to every label instead of an icon.
type LucideIconMap = Record<string, React.ComponentType<{ className?: string }>>;
function NavIcon({ name }: { name?: string }) {
  if (!name) return null;
  const Icon = (LucideIcons as unknown as LucideIconMap)[name];
  return Icon ? <Icon className="w-5 h-5" /> : null;
}

export default function AppSidebar() {
  const { isExpanded, isHovered, isMobileOpen, setIsHovered } = useSidebar();
  const { user } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});

  const userRole = user?.roles.includes('admin')
    ? 'admin'
    : user?.roles.includes('manager')
      ? 'manager'
      : user?.roles?.[0];

  // The menu follows the Permissions page: a page whose module was switched off for this role disappears (and a custom role sees the pages
  // it was granted). Admin is never restricted, so it skips the request.
  const isAdminRole = userRole === 'admin';
  const permissions = useMyPermissions(!!user && !isAdminRole);
  const myPermissions = permissions.data;

  // Real effective feature set for the signed-in tenant (plan features + any admin override —
  // see TenantSubscriptionProvider). Hides nav items whose module the tenant's plan doesn't
  // include; `admin` bypasses this in featureMatches() the same way it bypasses role checks.
  const { features } = useBilling();
  const subscription = useTenantSubscription();

  // No flicker: the menu depends on two things that arrive a moment after the page (the role's permissions and the plan's features). Drawing it
  // from whatever is there first meant every entry appeared and then some vanished. Instead: admin (never restricted) draws at once; anyone
  // else draws from the menu remembered from their last visit (so a returning user sees the right menu on the very first paint) or, on a
  // first visit, a placeholder — and switches to the live answer only once BOTH have arrived.
  const menuCacheKey = user?.id ? `sv-menu:${user.id}` : null;
  const [rememberedKeys] = useState<string[] | null>(() => {
    try {
      const raw = menuCacheKey ? localStorage.getItem(menuCacheKey) : null;
      return raw ? (JSON.parse(raw) as string[]) : null;
    } catch {
      return null;
    }
  });
  const settled =
    isAdminRole ||
    ((permissions.isSuccess || permissions.isError) && (subscription.isSuccess || subscription.isError));

  // Get routes grouped by category for the current user role. The shared ROUTES registry
  // also holds customer-app routes (e.g. DASHBOARD at /dashboard, layout: 'user') that
  // happen to share a category with an admin route (ADMIN_DASHBOARD at /, category
  // 'Overview') — without this filter both show up here as two separate "Dashboard" links.
  const routesGrouped = Object.fromEntries(
    Object.entries(getRoutesGroupedByCategory(userRole, myPermissions)).map(([category, routes]) => [
      category,
      routes.filter((route) => route.layout === 'admin' && featureMatches(route, features, userRole)),
    ])
  );

  const isActive = useCallback(
    (path: string) => {
      // Handle root path
      if (path === '/') {
        return pathname === '/';
      }
      // Check if current path matches or starts with the route path
      return pathname === path || pathname.startsWith(`${path}/`);
    },
    [pathname]
  );

  useEffect(() => {
    if (!openSubmenu) return;
    const el = submenuRefs.current[openSubmenu];
    if (el) {
      setSubmenuHeights((prev) => ({ ...prev, [openSubmenu]: el.scrollHeight }));
    }
  }, [openSubmenu]);

  const sidebarWidth =
    isExpanded || isHovered || isMobileOpen ? "w-[290px]" : "w-[90px]";

  // Helper function to check if a route has active subitems
  const hasActiveSubItem = (route: any) => {
    if (!route.subItems) return false;
    return route.subItems.some((subItem: any) => isActive(subItem.path));
  };

  // Toggle submenu
  const toggleSubmenu = (path: string) => {
    setOpenSubmenu(openSubmenu === path ? null : path);
  };

  // Remember the settled menu for next time (not for admin: nothing to wait for there).
  useEffect(() => {
    if (!settled || isAdminRole || !menuCacheKey) return;
    try {
      localStorage.setItem(menuCacheKey, JSON.stringify(Object.values(routesGrouped).flat().map((r) => r.key)));
    } catch {
      /* private mode — the placeholder path still works */
    }
  });

  // Before the live answer: the remembered menu if there is one, else nothing (the placeholder below).
  const shownGrouped = settled
    ? routesGrouped
    : rememberedKeys
      ? Object.fromEntries(
          Object.entries(routesGrouped).map(([c, rs]) => [c, rs.filter((r) => rememberedKeys.includes(r.key))]),
        )
      : {};
  const showPlaceholder = !settled && !rememberedKeys;

  // Filter out categories that have no routes
  const filteredCategories = Object.entries(shownGrouped).filter(
    ([_, routes]) => routes.length > 0
  );

  return (
    <aside
      className={`fixed top-0 left-0 mt-16 lg:mt-0 h-screen h-dvh ${sidebarWidth}
        bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 z-50 transition-all duration-300
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div className="py-8 flex justify-center lg:justify-start px-5">
        <BrandLogo compact={!(isExpanded || isHovered || isMobileOpen)} />
      </div>

      {/* Navigation */}
      <nav className="px-3 space-y-6 overflow-y-auto overscroll-contain h-[calc(100vh-8rem)] h-[calc(100dvh-8rem)] pb-16 lg:pb-0">
        {showPlaceholder && (
          <div className="space-y-3 px-3 pt-2" aria-busy="true" aria-label="Loading menu">
            {Array.from({ length: 14 }, (_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" style={{ width: `${65 + ((i * 17) % 30)}%` }} />
            ))}
          </div>
        )}
        {filteredCategories.map(([category, routes]) => (
          <div key={category}>
            <h2 className="text-xs uppercase text-gray-400 dark:text-gray-500 mb-3 flex justify-center lg:justify-start px-3">
              {isExpanded || isHovered || isMobileOpen ? (
                category
              ) : (
                <HorizontaLDots className="w-5 h-5" />
              )}
            </h2>

            <ul className="space-y-2">
              {routes.map((route) => {
                const isOpen = openSubmenu === route.path;
                const isActiveRoute = isActive(route.path) || hasActiveSubItem(route);
                const hasSubItems = route.subItems && route.subItems.length > 0;

                return (
                  <li key={route.path}>
                    {hasSubItems ? (
                      <>
                        <button
                          onClick={() => toggleSubmenu(route.path)}
                          className={`w-full flex items-center px-3 py-3 rounded-lg transition-all duration-200
                            ${isActiveRoute 
                              ? 'bg-primary/10 text-primary dark:bg-primary/20' 
                              : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                          <span className="flex items-center justify-center w-6 h-6">
                            <NavIcon name={route.icon} />
                          </span>
                          
                          {(isExpanded || isHovered || isMobileOpen) && (
                            <>
                              <span className="ml-3 text-sm font-medium truncate">
                                {route.label}
                              </span>
                              <ChevronDownIcon
                                className={`ml-auto w-4 h-4 transition-transform duration-200 ${
                                  isOpen ? "rotate-180" : ""
                                }`}
                              />
                            </>
                          )}
                        </button>

                        {/* Submenu */}
                        <div
                          ref={(el) => {
                            submenuRefs.current[route.path] = el;
                          }}
                          className="overflow-hidden transition-all duration-300"
                          style={{
                            height: isOpen ? submenuHeights[route.path] || 0 : 0,
                          }}
                        >
                          <ul className="ml-8 mt-1 space-y-1">
                            {route.subItems?.map((subItem) => (
                              <li key={subItem.path}>
                                <Link
                                  to={subItem.path}
                                  className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-200
                                    ${isActive(subItem.path)
                                      ? "text-primary bg-primary/10 dark:bg-primary/20"
                                      : "text-gray-600 dark:text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800"
                                    }`}
                                >
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <Link
                        to={route.path}
                        className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200
                          ${isActiveRoute
                            ? 'bg-primary/10 text-primary dark:bg-primary/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }`}
                      >
                        <span className="flex items-center justify-center w-6 h-6">
                          <NavIcon name={route.icon} />
                        </span>
                        
                        {(isExpanded || isHovered || isMobileOpen) && (
                          <span className="ml-3 text-sm font-medium truncate">
                            {route.label}
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Platform admin only — deliberately NOT registered in shared-core's ROUTES config.
            roleMatches() there has an unconditional "role === UserRole.ADMIN bypasses every
            role check" shortcut (by design, for every *other* admin route) that would let a
            store's own "admin" see this section too if it went through the normal
            getRoutesGroupedByCategory path — is_platform_admin is a strictly separate,
            higher-privilege claim (see JwtTokenService.IssuePairAsync), never satisfied by a
            bare "admin" role, so it needs its own gate here instead. */}
        {user?.isPlatformAdmin && (
          <div>
            <h2 className="text-xs uppercase text-gray-400 dark:text-gray-500 mb-3 flex justify-center lg:justify-start px-3">
              {isExpanded || isHovered || isMobileOpen ? 'Platform' : <HorizontaLDots className="w-5 h-5" />}
            </h2>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/platform/stores"
                  className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200
                    ${isActive('/platform/stores')
                      ? 'bg-primary/10 text-primary dark:bg-primary/20'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                >
                  <span className="flex items-center justify-center w-6 h-6">
                    <NavIcon name="Building2" />
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="ml-3 text-sm font-medium truncate">Stores</span>
                  )}
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </aside>
  );
}